import React, { useMemo, useRef } from "react";
import { User } from "@supabase/supabase-js";
import { useToast } from "@/hooks/use-toast";
import { useProfileContext } from "@/contexts/ProfileContext";
import { useAboutMeState } from "./useAboutMeState";
import BioSection from "./BioSection";
import AboutMeHeader from "./components/AboutMeHeader";
import AboutMeGrid from "./components/AboutMeGrid";
import AboutMeAutoSave from "./components/AboutMeAutoSave";
import EducationLanguagesCard from "./EducationLanguagesCard";
import SkillsSection from "./SkillsSection";
import AvatarSection from "./AvatarSection";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EnhancedAvatarUploadRef } from "../enhanced-avatar";
import { DISCIPLINES } from "@/data/constants";
import { ALL_COUNTRIES } from "@/data/countries";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getFlagEmoji } from "@/utils/countryFlags";

interface AboutMeSectionProps {
  user: User;
  bio?: string | null;
  location?: string | null;
  profession?: string | null;
  skills?: string[] | null;
  languages?: string[] | null;
  education?: string | null;
  years_experience?: number | null;
  avatar_url?: string | null;
  display_name?: string | null;
  username?: string | null;
  activeSection?: string;
  onAboutMeChange: (data: {
    bio?: string | null;
    location?: string | null;
    profession?: string | null;
    skills?: string[] | null;
    languages?: string[] | null;
    education?: string | null;
    years_experience?: number | null;
    avatar_url?: string | null;
    display_name?: string | null;
    username?: string | null;
  }) => void;
}

const AboutMeSection: React.FC<AboutMeSectionProps> = ({
  user,
  bio = "",
  location = "",
  profession = "",
  skills = [],
  languages = [],
  education = "",
  years_experience = null,
  avatar_url = null,
  display_name = "",
  username = "",
  activeSection = "about",
  onAboutMeChange,
}) => {
  const { toast } = useToast();
  const { isUpdating } = useProfileContext();
  const avatarUploadRef = useRef<EnhancedAvatarUploadRef>(null);

  const {
    bioValue,
    locationValue,
    professionValue,
    skillsValue,
    languagesValue,
    educationValue,
    experienceLevel,
    isGeneratingBio,
    avatarValue,
    avatarFile,
    processedBlob,
    isUploadingAvatar,
    displayNameValue,
    usernameValue,
    setBioValue,
    setLocationValue,
    setProfessionValue,
    setSkillsValue,
    setLanguagesValue,
    setEducationValue,
    setExperienceLevel,
    setIsGeneratingBio,
    handleAvatarChange,
    getYearsFromExperienceLevel,
    setDisplayNameValue,
    setUsernameValue,
    getData,
  } = useAboutMeState({
    initialBio: bio,
    initialLocation: location,
    initialProfession: profession,
    initialSkills: skills,
    initialLanguages: languages,
    initialEducation: education,
    initialYearsExperience: years_experience,
    initialAvatarUrl: avatar_url,
    initialDisplayName: display_name,
    initialUsername: username,
    sectionId: activeSection,
  });

  // Check if there are any unsaved changes by comparing current values with initial values
  const hasUnsavedChanges = useMemo(() => {
    return (
      bioValue !== bio ||
      locationValue !== location ||
      professionValue !== profession ||
      JSON.stringify(skillsValue) !== JSON.stringify(skills) ||
      JSON.stringify(languagesValue) !== JSON.stringify(languages) ||
      educationValue !== education ||
      getYearsFromExperienceLevel(experienceLevel) !== years_experience ||
      displayNameValue !== display_name ||
      usernameValue !== username ||
      avatarValue !== avatar_url
    );
  }, [
    bioValue,
    bio,
    locationValue,
    location,
    professionValue,
    profession,
    skillsValue,
    skills,
    languagesValue,
    languages,
    educationValue,
    education,
    experienceLevel,
    years_experience,
    displayNameValue,
    display_name,
    usernameValue,
    username,
    avatarValue,
    avatar_url,
    getYearsFromExperienceLevel,
  ]);

  const handleSave = async () => {
    console.log("[handleSave] Starting save process");
    const updatedProfile = getData();

    // Check if avatar is a blob URL (not yet uploaded to Supabase)
    if (updatedProfile.avatar_url && updatedProfile.avatar_url.startsWith('blob:')) {
      console.log("[handleSave] Detected blob URL, uploading to Supabase first");

      // Upload the pending avatar to Supabase first
      if (avatarUploadRef.current?.hasPendingUpload) {
        try {
          const uploadedUrl = await avatarUploadRef.current.uploadPendingAvatar();
          if (uploadedUrl) {
            console.log("[handleSave] Avatar uploaded successfully:", uploadedUrl);
            updatedProfile.avatar_url = uploadedUrl;
          } else {
            console.error("[handleSave] Avatar upload returned null");
            toast({
              title: "Upload failed",
              description: "Could not upload profile picture",
              variant: "destructive",
            });
            return; // Don't save if avatar upload failed
          }
        } catch (error) {
          console.error("[handleSave] Avatar upload failed:", error);
          // Error toast is already shown by uploadPendingAvatar
          return; // Don't save if avatar upload failed
        }
      }
    } else {
      console.log("[handleSave] Avatar is already a Supabase URL or null, no upload needed");
    }

    // Save the profile with the real Supabase URL
    console.log("[handleSave] Saving profile with avatar URL:", updatedProfile.avatar_url);
    onAboutMeChange(updatedProfile);
    // Note: Success/error feedback is handled by ProfileSectionHandler
  };

  // When user adds/removes a language:
  const handleLanguagesChange = (newLanguages: string[]) => {
    setLanguagesValue(newLanguages);
    onAboutMeChange({
      languages: newLanguages,
    });
  };

  return (
    <div className="space-y-6">
      <AboutMeAutoSave activeSection={activeSection} />

      <AboutMeHeader
        hasUnsavedChanges={hasUnsavedChanges}
        onSave={handleSave}
        isSaving={isUpdating}
      />

      <div className="grid grid-cols-1 md:auto-rows-min gap-6">
        {/* Left column - Basic Information */}
        <Card className="">
          <CardHeader>
            <CardTitle className="text-lg">Basic Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-12">
              {/* Left side - 2/3 width */}
              <div className="md:col-span-2 space-y-4">
                  <div>
                    <label className="text-sm font-medium">Display Name</label>
                    <input
                      type="text"
                      value={displayNameValue}
                      onChange={(e) => setDisplayNameValue(e.target.value)}
                      className="w-full mt-1 px-3 py-2 border rounded-md"
                      placeholder="Your display name"
                    />
                  </div>

                  {/* Location Section */}
                  <div>
                    <label className="text-sm font-medium">Where are you based?</label>
                    <Select
                      onValueChange={setLocationValue}
                      value={locationValue || ""}
                    >
                      <SelectTrigger className="w-full mt-1 rounded-md border">
                        {(() => {
                          const locationLower = locationValue
                            ? locationValue.toLowerCase()
                            : "";
                          const country = ALL_COUNTRIES.find(
                            (c) =>
                              c.id.toLowerCase() === locationLower ||
                              c.label.toLowerCase() === locationLower
                          );
                          const flag = country
                            ? getFlagEmoji(country.label) ||
                              getFlagEmoji(country.id)
                            : "";
                          return country ? (
                            <span className="inline-flex items-center gap-2">
                              {flag && (
                                <span className="text-base leading-none">
                                  {flag}
                                </span>
                              )}
                              <span className="ml-2">{country.label}</span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground">
                              Select your location
                            </span>
                          );
                        })()}
                      </SelectTrigger>
                      <SelectContent>
                        {ALL_COUNTRIES.map((country) => {
                          const flag =
                            getFlagEmoji(country.label) ||
                            getFlagEmoji(country.id);
                          return (
                            <SelectItem key={country.id} value={country.id}>
                              <span className="inline-flex items-center gap-2">
                                {flag && (
                                  <span className="text-base leading-none">
                                    {flag}
                                  </span>
                                )}
                                <span>{country.label}</span>
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Profession Section */}
                  <div>
                    <label className="text-sm font-medium">Profession</label>
                    <Select
                      onValueChange={setProfessionValue}
                      value={professionValue || ""}
                    >
                      <SelectTrigger className="w-full mt-1 rounded-md border">
                        <SelectValue placeholder="Select your profession" />
                      </SelectTrigger>
                      <SelectContent>
                        {DISCIPLINES.map((discipline) => (
                          <SelectItem
                            key={discipline.id}
                            value={discipline.label}
                          >
                            {discipline.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Bio Section */}
                  <BioSection
                    bioValue={bioValue}
                    professionValue={professionValue}
                    skillsValue={skillsValue}
                    languagesValue={languagesValue}
                    locationValue={locationValue}
                    educationValue={educationValue}
                    experienceLevel={experienceLevel}
                    getYearsFromExperienceLevel={getYearsFromExperienceLevel}
                    isGeneratingBio={isGeneratingBio}
                    setBioValue={setBioValue}
                    setIsGeneratingBio={setIsGeneratingBio}
                  />
                </div>

              {/* Right side - 1/3 width - Profile Picture */}
              <div className="md:col-span-1">
                <AvatarSection
                  ref={avatarUploadRef}
                  user={user}
                  avatarUrl={avatarValue}
                  isUploading={isUploadingAvatar}
                  onAvatarChange={handleAvatarChange}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Right column - Expertise */}
        <Card className="">
          <CardHeader>
            <CardTitle className="text-lg">Expertise</CardTitle>
          </CardHeader>
          <CardContent className="">
            <div className="space-y-4 max-w-2xl">
              {/* Education and Languages */}
              <EducationLanguagesCard
                educationValue={educationValue}
                experienceLevel={experienceLevel}
                languagesValue={languagesValue}
                setLanguagesValue={handleLanguagesChange}
                setEducationValue={setEducationValue}
                setExperienceLevel={setExperienceLevel}
              />

              {/* Skills */}
              <div>
                <label className="text-sm font-medium mb-2 block">Skills</label>
                <SkillsSection
                  skillsValue={skillsValue}
                  professionValue={professionValue}
                  setSkillsValue={setSkillsValue}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AboutMeSection;
